import { Autocomplete, CircularProgress, TextField } from "@mui/material"
import { useEffect, useMemo, useState } from "react";
import { Environment } from "../../environment";
import { TipoDeCaixa_Service } from "../../services/api/tipo_de_caixa/TipoDeCaixa_Service";
import { AlertSnackBar } from "../alert/AlertSnackBar";
import { useField } from "@unform/core";

type AutoCompleteOption = {
    id: number; // apenas algo que diferencie as opções, o valor q vai ser retornado ao escolher
    label: string;
}

interface IAutoComplete {
    /**
     * Deixa o autocomplete desabilitado
     */
    disabled?: boolean,
    /**
     * Fazer com que tenha a indicação que está carregando 
     */
    isExternalLoading?: boolean,
    /**
     * Altera o size do campo (para cadastro o tamanho é médio)
     */
    paraCadastro?: boolean,
    /**
     * Width do campo(padrão 200), null para não ter width e variar de acordo com a tela
     */
    width?: number | null,

    /**
     * Valor a ser pré-selecionado
     */
    value?: number
    /**
     * Texto para quando der erro
     */
    helperText?: string
    /**
     * Pegar o valor que vai retornar ao trocar opção
     */
    selectedIdTipoDeCaixa?: (idTipoDeCaixa: number | undefined) => void,
}

/**
 * selectedIdTipoDeCaixa={selectedIdTipoDeCaixa => *nomeUseState*(selectedIdTipoDeCaixa)} \
 * para pegar o valor selecionado e colocar em uma variável/state
 */

export const AutoCompleteDeTipoDeCaixa: React.FC<IAutoComplete> = ({ disabled = false, isExternalLoading = false, paraCadastro = false, width = 230, selectedIdTipoDeCaixa, value = undefined, helperText = undefined }) => {

    const { fieldName, registerField, defaultValue, error, clearError } = useField('idTipoCaixa')

    const [selectedId, setSelectedId] = useState<number | null | undefined>(defaultValue);

    const [opcoes, setOpcoes] = useState<AutoCompleteOption[]>([]);
    const [isLoading, setIsLoading] = useState(isExternalLoading);

    const [alertSnackbar, setAlertSnackbar] = useState<{ tipoAlert: string, mensagem: string } | undefined>(undefined);
    const [duracaoAlert, setDuracaoAlert] = useState<number>(Environment.DURACAO_ALERT + 500);

    useEffect(() => {
        registerField({
            name: fieldName,// nome do campo
            getValue: () => selectedId,
            setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
        });
    }, [registerField, fieldName, selectedId]);

    useEffect(() => { // consulta para o autocomplete

        setIsLoading(true);
        TipoDeCaixa_Service.list().then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
                setOpcoes([]);
                setSelectedId(undefined);

                setAlertSnackbar({ tipoAlert: "erro", mensagem: result.message });
                setTimeout(() => {
                    setAlertSnackbar(undefined);
                }, duracaoAlert);

            } else {
                setOpcoes(result.map(tipoCaixa => ({ id: tipoCaixa.idTipoCaixa, label: tipoCaixa.nomeTipo })));
            }
        });

    }, []);

    const autoCompleteSelectedOption = useMemo(() => {
        if (!selectedId) return null;

        const selectedOption = opcoes.find(opcao => opcao.id === selectedId);
        if (!selectedOption) return null;

        return selectedOption;
    }, [selectedId, opcoes, value]);

    return (
        <>
            {alertSnackbar !== undefined && (
                <>
                    {alertSnackbar.tipoAlert === "sucesso" && (
                        <AlertSnackBar gravidade="success" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
                    )}
                    {alertSnackbar.tipoAlert === "erro" && (
                        <AlertSnackBar gravidade="error" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
                    )}
                    {alertSnackbar.tipoAlert === "aviso" && (
                        <AlertSnackBar gravidade="warning" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
                    )}
                </>
            )}
            <Autocomplete
                //traduzir textos
                openText="Abrir"
                closeText="Fechar"
                noOptionsText="Sem opções"
                loadingText="Carregando..."

                disablePortal

                //estilização
                //sx={{ width: width }}
                disabled={isExternalLoading || isLoading || disabled}
                loading={isLoading}
                popupIcon={isExternalLoading || isLoading ? <CircularProgress size={20} /> : undefined}//trocar o icone enquanto carrega os dados

                //dados
                options={opcoes}
                onChange={(_, newValue) => {
                    setSelectedId?.(newValue?.id);
                    clearError();
                }}
                value={autoCompleteSelectedOption}
                renderInput={(params) => <TextField {...params} label="Tipo de Caixa" placeholder="Digite o nome..." fullWidth size={paraCadastro ? 'medium' : 'small'} helperText={error} error={!!error} />}
            />
        </>
    );
}